import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { filamentCompareData, formatBendStrength, formatFlexuralModulus } from '../../../data/filamentCompare';
import { RatingStars } from '../../../components/base/RatingStars';
import { useSEO } from '../../../hooks/useSEO';
import { StressStrainChart } from '../../../components/feature/StressStrainChart';
import { fetchAllManufacturersData, selectBestManufacturer, type FilamentTestData } from '../../../lib/filamentStressStrain';
import { getFilamentName, getFilamentType } from '../../../data/filamentTestData';

type SortKey = 'none' | 'printTemp' | 'warping' | 'strength' | 'modulus' | 'creep' | 'heat';
type SortOrder = 'asc' | 'desc';
type Pattern = 'pattern1' | 'pattern2' | 'pattern3';

const FilamentComparePage: React.FC = () => {
  const location = useLocation();
  const isEnglish = location.pathname === '/en/filament/compare' || location.pathname === '/en/filament/compare/';

  const TEXT = isEnglish ? {
    pageTitle: 'Filament Comparison Table',
    pageDescription: 'Compare the performance of 3D printer filaments at a glance. Check printing temperature, warping, strength, creep resistance, and heat resistance with star ratings and numerical values.',
    selectFilaments: 'Select Filaments to Compare',
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    selectedFilaments: 'Selected Filaments:',
    selectFilamentsPrompt: 'Please select filaments',
    addFilaments: 'Add Filaments:',
    filamentColumn: 'Filament',
    printTempColumn: 'Print Temp',
    warpingColumn: 'Warping',
    strengthColumn: 'Strength',
    modulusColumn: 'Flex. Modulus',
    creepColumn: 'Creep Resistance',
    heatColumn: 'Heat Resistance',
    removeColumn: 'Remove',
    sortInfo: 'Click column headers to sort. Click "Filament" to restore original order.',
    materialColumn: 'Material',
    tempColumn: 'Temp',
    sortInfoMobile: 'Tap column headers to sort. Tap "Material" to restore.',
    nozzle: 'Nozzle',
    bed: 'Bed',
    notEntered: 'Not entered',
    notMeasured: 'Not measured',
    bendStrength: 'Bend strength',
    flexuralModulus: 'Flex. modulus',
    heatResistanceTemp: 'Heat resistance temp',
    remove: 'Remove',
    noFilamentsSelected: 'Please select filaments to compare',
    ratingCriteria: 'Rating Criteria',
    printTempCriteria: 'Print Temperature: Lower temperature = higher rating (★5=low temp, ★1=high temp)',
    warpingCriteria: 'Warping: Less warping = higher rating (★5=minimal warping, ★1=high warping)',
    strengthCriteria: 'Strength: Higher bend strength = higher rating (★5=very strong, ★1=weak)',
    creepCriteria: 'Creep Resistance: Better long-term load resistance = higher rating (★5=high resistance, ★1=low resistance)',
    heatCriteria: 'Heat Resistance: Higher temperature tolerance = higher rating (★5=very high, ★1=low)',
    notEnteredCriteria: 'Not entered: ☆☆☆☆☆ + "Not entered" or "Not measured" displayed',
    stressStrainTitle: 'Stress–Strain Curve',
    stressStrainDescription: 'Compare bending test results of selected filaments with stress–strain curves. Horizontal axis shows strain (%), vertical axis shows stress (MPa). Each filament displays data from the highest priority manufacturer.',
    measurementDirection: 'Measurement Direction:',
    pattern1: 'Pattern 1',
    pattern2: 'Pattern 2',
    pattern3: 'Pattern 3',
    noData: '(No data)',
    loadingData: 'Loading data...',
    noStressStrainData: 'No bending test data ({pattern}) available for selected filaments. Graphs will be displayed when data is added.',
    partialDataInfo: '{count} filament(s) have no bending test data ({pattern}) and are not displayed in the graph.',
    dataUsageTitle: 'Data Usage',
    dataUsageText: 'Personal use is free, but if you wish to publish in videos, articles, SNS, or redistribute data, please contact us in advance. See {link} for details.',
    dataUsageLink: 'Data Usage Policy',
    notesTitle: 'Notes',
    note1: 'Ratings indicate general trends and may vary by manufacturer and grade.',
    note2: 'Stress–strain curves are calculated from 3-point bending test results.',
    note3: 'Results may vary depending on test conditions (span, specimen dimensions, speed, etc.).',
    note4: '"Not entered" and "Not measured" items may be updated with data in the future.',
    note5: 'For detailed properties and print settings, please check individual filament pages.',
    backToList: 'Back to Filament List',
    downloadPageTitle: 'Download Raw Data',
    downloadPageDesc: 'The raw CSV data used in the graphs above is available for download.'
  } : {
    pageTitle: 'フィラメント比較表',
    pageDescription: '3Dプリンター用フィラメントの性能を一覧で比較できます。印刷温度、反り、強度、クリープ耐性、耐熱性の5項目を★評価と数値で確認できます。',
    selectFilaments: '比較対象フィラメントを選択',
    selectAll: 'ALL選択',
    deselectAll: 'ALL解除',
    selectedFilaments: '選択中のフィラメント：',
    selectFilamentsPrompt: 'フィラメントを選択してください',
    addFilaments: 'フィラメントを追加：',
    filamentColumn: 'フィラメント',
    printTempColumn: '印刷温度',
    warpingColumn: '反り',
    strengthColumn: '強度',
    modulusColumn: '弾性率',
    creepColumn: 'クリープ耐性',
    heatColumn: '耐熱',
    removeColumn: '解除',
    sortInfo: '列見出しをクリックすると並び替えができます。「フィラメント」をクリックすると元の順序に戻ります。',
    materialColumn: '材料',
    tempColumn: '温度',
    sortInfoMobile: '列見出しタップで並び替え。「材料」で元に戻る。',
    nozzle: 'ノズル',
    bed: 'ベッド',
    notEntered: '未入力',
    notMeasured: '未測定',
    bendStrength: '曲げ強度',
    flexuralModulus: '曲げ弾性率',
    heatResistanceTemp: '耐熱温度',
    remove: '削除',
    noFilamentsSelected: '比較するフィラメントを選択してください',
    ratingCriteria: '評価基準',
    printTempCriteria: '印刷温度：低温で印刷できるほど高評価（★5=低温、★1=高温）',
    warpingCriteria: '反り：反りにくいほど高評価（★5=反りにくい、★1=反りやすい）',
    strengthCriteria: '強度：曲げ強度が高いほど高評価（★5=非常に強い、★1=弱い）',
    creepCriteria: 'クリープ耐性：長期荷重に強いほど高評価（★5=耐性高い、★1=耐性低い）',
    heatCriteria: '耐熱：高温に耐えるほど高評価（★5=非常に高い、★1=低い）',
    notEnteredCriteria: '未入力：☆☆☆☆☆ + 「未入力」または「未測定」と表示',
    stressStrainTitle: '応力–ひずみ曲線（Stress–Strain）',
    stressStrainDescription: '選択されたフィラメントの曲げ試験結果を応力–ひずみ曲線で比較します。横軸はひずみ（%）、縦軸は応力（MPa）を示します。各フィラメントは優先順位最上位のメーカーデータを表示しています。',
    measurementDirection: '測定方向：',
    pattern1: 'パターン1',
    pattern2: 'パターン2',
    pattern3: 'パターン3',
    noData: '(データなし)',
    loadingData: 'データ読み込み中...',
    noStressStrainData: '選択されたフィラメントの曲げ試験データ（{pattern}）がありません。データが追加され次第、グラフが表示されます。',
    partialDataInfo: '{count}種類のフィラメントは曲げ試験データ（{pattern}）が未測定のため、グラフに表示されていません。',
    dataUsageTitle: 'データ利用について',
    dataUsageText: '個人利用は自由ですが、動画・記事・SNS等での公開、データの転載・再配布を行う場合は必ず事前にお問い合わせください。詳細は{link}をご確認ください。',
    dataUsageLink: 'データ利用規約',
    notesTitle: 'ご注意',
    note1: '評価は一般的な傾向を示すものであり、メーカーやグレードによって異なる場合があります。',
    note2: '応力–ひずみ曲線は3点曲げ試験の結果から計算されています。',
    note3: '試験条件（スパン、試験片寸法、速度など）により結果は変動します。',
    note4: '「未入力」「未測定」の項目は、今後データが追加される可能性があります。',
    note5: '詳細な特性や印刷設定は、各フィラメントの個別ページをご確認ください。',
    backToList: 'フィラメント一覧に戻る',
    downloadPageTitle: 'データダウンロードページ',
    downloadPageDesc: '上記グラフの元データ（CSV）はダウンロードページから入手できます。'
  };

  useSEO({
    title: isEnglish ? 'Filament Comparison Table | 3D Printer Filament Performance Comparison' : 'フィラメント比較表 | 3Dプリンター フィラメント性能比較',
    description: isEnglish ? 'Compare the performance of 3D printer filaments at a glance. Check printing temperature, warping, strength, creep resistance, and heat resistance with star ratings and numerical values. Includes 23 types: PLA, PETG, ABS, ASA, PC, PA, TPU, and more.' : '3Dプリンター用フィラメントの性能を一覧で比較。印刷温度、反り、強度、クリープ耐性、耐熱性を★評価と数値で確認できます。PLA、PETG、ABS、ASA、PC、PA、TPUなど全23種類を掲載。',
    keywords: isEnglish ? 'filament comparison,3D printer,performance comparison,print temperature,strength,heat resistance,PLA,PETG,ABS,ASA,PC,PA,TPU' : 'フィラメント比較,3Dプリンター,性能比較,印刷温度,強度,耐熱性,PLA,PETG,ABS,ASA,PC,PA,TPU',
    canonicalPath: isEnglish ? '/en/filament/compare' : '/filament/compare'
  });

  // 選択されたフィラメントのslugを管理（デフォルト: PLA, PETG）
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>(['pla', 'petg']);
  
  // ソート設定
  const [sortKey, setSortKey] = useState<SortKey>('none');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  
  // パターン選択
  const [selectedPattern, setSelectedPattern] = useState<Pattern>('pattern1');
  
  // Stress-Strain データのキャッシュ（pattern別）
  const [stressStrainCache, setStressStrainCache] = useState<Map<string, Map<Pattern, FilamentTestData | null>>>(new Map());
  
  // 読み込み中のslugを追跡
  const [loadingData, setLoadingData] = useState<Set<string>>(new Set());

  // ALL選択
  const selectAll = () => {
    setSelectedSlugs(filamentCompareData.map(f => f.slug));
  };

  // ALL選択解除
  const deselectAll = () => {
    setSelectedSlugs([]);
  };

  // フィラメントを追加
  const addFilament = (slug: string) => {
    if (!selectedSlugs.includes(slug)) {
      setSelectedSlugs([...selectedSlugs, slug]);
    }
  };

  // フィラメントを削除
  const removeFilament = (slug: string) => {
    setSelectedSlugs(selectedSlugs.filter(s => s !== slug));
  };

  // ノズル最低温度を抽出する関数
  const extractMinNozzleTemp = (tempStr: string): number | null => {
    const match = tempStr.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  };

  // ソート関数
  const sortFilaments = (filaments: typeof filamentCompareData) => {
    if (sortKey === 'none') return filaments;

    return [...filaments].sort((a, b) => {
      let aRating = 0;
      let bRating = 0;
      let aValue: number | null = null;
      let bValue: number | null = null;

      switch (sortKey) {
        case 'printTemp':
          aRating = a.printTempRating;
          bRating = b.printTempRating;
          aValue = extractMinNozzleTemp(a.nozzleTemp);
          bValue = extractMinNozzleTemp(b.nozzleTemp);
          break;
        case 'warping':
          aRating = a.warpingRating;
          bRating = b.warpingRating;
          // 反りは数値なし
          break;
        case 'strength':
          aRating = a.strengthRating;
          bRating = b.strengthRating;
          aValue = a.bendStrengthMPa ?? null;
          bValue = b.bendStrengthMPa ?? null;
          break;
        case 'modulus':
          aRating = a.strengthRating;
          bRating = b.strengthRating;
          aValue = a.flexuralModulusMPa ?? null;
          bValue = b.flexuralModulusMPa ?? null;
          break;
        case 'creep':
          aRating = a.creepResistanceRating;
          bRating = b.creepResistanceRating;
          // クリープ耐性は数値なし
          break;
        case 'heat':
          aRating = a.heatResistanceRating;
          bRating = b.heatResistanceRating;
          aValue = a.heatResistanceTemp ?? null;
          bValue = b.heatResistanceTemp ?? null;
          break;
      }

      // 未入力（★0）は末尾に
      if (aRating === 0 && bRating === 0) return 0;
      if (aRating === 0) return 1;
      if (bRating === 0) return -1;

      // 第一キー：★の数で比較
      if (aRating !== bRating) {
        if (sortOrder === 'desc') {
          return bRating - aRating;
        } else {
          return aRating - bRating;
        }
      }

      // 第二キー：同じ★の場合は数値でタイブレーク
      // 数値が無い場合は末尾に
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      // 数値で比較（印刷温度は低い方が良い=降順時は小さい値が上、昇順時は大きい値が上）
      if (sortKey === 'printTemp') {
        // 印刷温度は低温が高評価なので、降順時は低い温度が上に来るべき
        if (sortOrder === 'desc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      } else {
        // 強度・耐熱は高い方が良い=降順時は大きい値が上、昇順時は小さい値が上
        if (sortOrder === 'desc') {
          return bValue - aValue;
        } else {
          return aValue - bValue;
        }
      }
    });
  };

  // ヘッダークリックでソート
  const handleHeaderClick = (key: SortKey) => {
    if (key === 'none') {
      // フィラメント列をクリックした場合はソート解除
      setSortKey('none');
      setSortOrder('desc');
    } else {
      // パラメータ列をクリックした場合
      if (sortKey === key) {
        // 同じ列をクリックした場合は昇順/降順を切り替え
        setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
      } else {
        // 新しい列の場合は降順から開始
        setSortKey(key);
        setSortOrder('desc');
      }
    }
  };

  // 選択されたフィラメントのデータを取得してソート
  const selectedFilaments = sortFilaments(
    filamentCompareData.filter(f => selectedSlugs.includes(f.slug))
  );

  // 選択されたフィラメントのCSVデータを読み込む
  useEffect(() => {
    // 読み込み済みのslugを追跡
    const slugsToLoad = selectedSlugs.filter(slug => !stressStrainCache.has(slug) && !loadingData.has(slug));
    
    if (slugsToLoad.length === 0) {
      return;
    }

    // 読み込み開始マーク
    setLoadingData(prev => {
      const newSet = new Set(prev);
      slugsToLoad.forEach(slug => newSet.add(slug));
      return newSet;
    });

    // 並列読み込み
    Promise.all(
      slugsToLoad.map(async (slug) => {
        try {
          const filamentType = getFilamentType(slug);
          const filamentName = getFilamentName(slug);
          
          console.log(`[${slug}] 比較用データ読み込み開始:`, { filamentType });

          // 3パターン全て読み込み（各パターンで最優先メーカーのみ）
          const patterns: Pattern[] = ['pattern1', 'pattern2', 'pattern3'];
          const patternMap = new Map<Pattern, FilamentTestData | null>();

          for (const pattern of patterns) {
            // 全メーカーデータを取得（内部でキャッシュ利用）
            const allData = await fetchAllManufacturersData(filamentType, pattern);
            
            // 最優先メーカーを選択
            const bestData = selectBestManufacturer(allData);
            
            if (bestData) {
              // キャッシュを汚染しないようクローンを作成し、識別用のIDとしてslugを割り当て
              const bestDataClone = { ...bestData, filamentName: `${filamentName} (${bestData.maker})`, filamentId: slug };
              patternMap.set(pattern, bestDataClone);
              console.log(`[${slug}] ${pattern}: ${bestDataClone.maker} を採用`);
            } else {
              patternMap.set(pattern, null);
              console.log(`[${slug}] ${pattern}: データなし`);
            }
          }

          return { slug, patternMap };
        } catch (error) {
          console.error(`[${slug}] データ読み込みエラー:`, error);
          const patternMap = new Map<Pattern, FilamentTestData | null>();
          patternMap.set('pattern1', null);
          patternMap.set('pattern2', null);
          patternMap.set('pattern3', null);
          return { slug, patternMap };
        }
      })
    ).then(results => {
      // 一括でキャッシュ更新
      setStressStrainCache(prev => {
        const newCache = new Map(prev);
        results.forEach(({ slug, patternMap }) => {
          newCache.set(slug, patternMap);
        });
        return newCache;
      });

      // 読み込み完了マーク
      setLoadingData(prev => {
        const newSet = new Set(prev);
        slugsToLoad.forEach(slug => newSet.delete(slug));
        return newSet;
      });
    });
  }, [selectedSlugs]); // stressStrainCache と loadingData を依存から除外

  // グラフ用のデータセット（選択されたパターンのみ、測定済みのみ）
  const stressStrainDatasets = selectedSlugs
    .map(slug => {
      const patternMap = stressStrainCache.get(slug);
      if (!patternMap) return null;
      return patternMap.get(selectedPattern);
    })
    .filter((data): data is FilamentTestData => data !== null && data !== undefined);

  const breadcrumbItems = isEnglish ? [
    { label: 'Home', path: '/en' },
    { label: 'Filament Data', path: '/filament' },
    { label: 'Comparison' }
  ] : [
    { label: 'ホーム', path: '/' },
    { label: 'フィラメントデータ', path: '/filament' },
    { label: '比較表' }
  ];

  // パターン表示名
  const patternLabels: Record<Pattern, string> = {
    pattern1: TEXT.pattern1,
    pattern2: TEXT.pattern2,
    pattern3: TEXT.pattern3
  };



  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8">
            {/* タイトル */}
            <div className="text-center mt-8">
              <h1 className="text-4xl font-bold text-white font-['Exo'] mb-4">
                {TEXT.pageTitle}
              </h1>
              <div className="section-divider max-w-md mx-auto mb-6"></div>
              <p className="text-metal-light max-w-3xl mx-auto leading-relaxed text-sm sm:text-base">
                {TEXT.pageDescription}
              </p>
            </div>

            {/* 1) フィラメント選択UI */}
            <div className="metal-panel p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg sm:text-xl font-semibold text-white font-['Exo'] accent-line pb-2 flex items-center gap-2">
                  <i className="ri-checkbox-multiple-line text-accent"></i>
                  {TEXT.selectFilaments}
                </h2>
                {/* ALL選択/解除ボタン */}
                <div className="flex gap-2">
                  <button
                    onClick={selectAll}
                    className="px-3 py-1.5 bg-transparent border border-accent/60 hover:border-accent hover:bg-accent/10 text-accent text-xs sm:text-sm font-medium rounded transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-checkbox-multiple-line mr-1"></i>
                    {TEXT.selectAll}
                  </button>
                  <button
                    onClick={deselectAll}
                    className="px-3 py-1.5 bg-transparent border border-metal-light/60 hover:border-metal-light hover:bg-metal-light/10 text-metal-light hover:text-white text-xs sm:text-sm font-medium rounded transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-checkbox-blank-line mr-1"></i>
                    {TEXT.deselectAll}
                  </button>
                </div>
              </div>
              
              {/* 選択済みフィラメント */}
              <div className="mb-4">
                <p className="text-xs sm:text-sm text-metal-light mb-3">{TEXT.selectedFilaments}</p>
                <div className="flex flex-wrap gap-2">
                  {selectedSlugs.length === 0 ? (
                    <span className="text-metal-light text-xs sm:text-sm">{TEXT.selectFilamentsPrompt}</span>
                  ) : (
                    selectedSlugs.map(slug => {
                      const filament = filamentCompareData.find(f => f.slug === slug);
                      return filament ? (
                        <div 
                          key={slug}
                          className="flex items-center gap-2 bg-metal/50 border border-metal-light/30 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg"
                        >
                          <span className="text-white text-xs sm:text-sm font-medium">{filament.name}</span>
                          <button
                            onClick={() => removeFilament(slug)}
                            className="text-metal-light hover:text-accent-orange transition-colors cursor-pointer"
                            aria-label={`${filament.name}を削除`}
                          >
                            <i className="ri-close-line text-sm sm:text-base"></i>
                          </button>
                        </div>
                      ) : null;
                    })
                  )}
                </div>
              </div>

              {/* フィラメント追加 */}
              <div>
                <p className="text-xs sm:text-sm text-metal-light mb-3">{TEXT.addFilaments}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {filamentCompareData.map(filament => {
                    const isSelected = selectedSlugs.includes(filament.slug);
                    return (
                      <button
                        key={filament.slug}
                        onClick={() => isSelected ? removeFilament(filament.slug) : addFilament(filament.slug)}
                        className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                          isSelected
                            ? 'bg-accent/20 border border-accent text-accent'
                            : 'bg-transparent border border-metal-light/40 text-metal-light hover:border-metal-light hover:bg-metal-light/10 hover:text-white'
                        }`}
                      >
                        {isSelected && <i className="ri-check-line mr-1"></i>}
                        {filament.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* 2) フィラメント比較表 */}
            {selectedFilaments.length > 0 ? (
              <>
                {/* デスクトップ表示 */}
                <div className="tech-card overflow-hidden hidden sm:block">
                  <div className="flex">
                    {/* 表部分 */}
                    <div className="flex-1 overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-[#3a3d42]">
                            <th 
                              onClick={() => handleHeaderClick('none')}
                              className="px-3 py-3 text-left text-sm font-semibold text-white font-['Exo'] whitespace-nowrap cursor-pointer hover:bg-metal/50 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <span>{TEXT.filamentColumn}</span>
                                {sortKey === 'none' && (
                                  <i className="ri-refresh-line text-white text-base bg-accent/20 rounded-full w-5 h-5 flex items-center justify-center"></i>
                                )}
                              </div>
                            </th>
                            <th 
                              onClick={() => handleHeaderClick('printTemp')}
                              className="px-3 py-3 text-left text-sm font-semibold text-white font-['Exo'] whitespace-nowrap cursor-pointer hover:bg-metal/50 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <span>{TEXT.printTempColumn}</span>
                                {sortKey === 'printTemp' && (
                                  <i className={`text-white text-base bg-accent/20 rounded-full w-5 h-5 flex items-center justify-center ${sortOrder === 'desc' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'}`}></i>
                                )}
                              </div>
                            </th>
                            <th 
                              onClick={() => handleHeaderClick('warping')}
                              className="px-3 py-3 text-left text-sm font-semibold text-white font-['Exo'] whitespace-nowrap cursor-pointer hover:bg-metal/50 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <span>{TEXT.warpingColumn}</span>
                                {sortKey === 'warping' && (
                                  <i className={`text-white text-base bg-accent/20 rounded-full w-5 h-5 flex items-center justify-center ${sortOrder === 'desc' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'}`}></i>
                                )}
                              </div>
                            </th>
                            <th 
                              onClick={() => handleHeaderClick('strength')}
                              className="px-3 py-3 text-left text-sm font-semibold text-white font-['Exo'] whitespace-nowrap cursor-pointer hover:bg-metal/50 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <span>{TEXT.strengthColumn}</span>
                                {sortKey === 'strength' && (
                                  <i className={`text-white text-base bg-accent/20 rounded-full w-5 h-5 flex items-center justify-center ${sortOrder === 'desc' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'}`}></i>
                                )}
                              </div>
                            </th>
                            <th 
                              onClick={() => handleHeaderClick('modulus')}
                              className="px-3 py-3 text-left text-sm font-semibold text-white font-['Exo'] whitespace-nowrap cursor-pointer hover:bg-metal/50 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <span>{TEXT.modulusColumn}</span>
                                {sortKey === 'modulus' && (
                                  <i className={`text-white text-base bg-accent/20 rounded-full w-5 h-5 flex items-center justify-center ${sortOrder === 'desc' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'}`}></i>
                                )}
                              </div>
                            </th>
                            <th 
                              onClick={() => handleHeaderClick('creep')}
                              className="px-3 py-3 text-left text-sm font-semibold text-white font-['Exo'] whitespace-nowrap cursor-pointer hover:bg-metal/50 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <span>{TEXT.creepColumn}</span>
                                {sortKey === 'creep' && (
                                  <i className={`text-white text-base bg-accent/20 rounded-full w-5 h-5 flex items-center justify-center ${sortOrder === 'desc' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'}`}></i>
                                )}
                              </div>
                            </th>
                            <th 
                              onClick={() => handleHeaderClick('heat')}
                              className="px-3 py-3 text-left text-sm font-semibold text-white font-['Exo'] whitespace-nowrap cursor-pointer hover:bg-metal/50 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <span>{TEXT.heatColumn}</span>
                                {sortKey === 'heat' && (
                                  <i className={`text-white text-base bg-accent/20 rounded-full w-5 h-5 flex items-center justify-center ${sortOrder === 'desc' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'}`}></i>
                                )}
                              </div>
                            </th>
                            <th className="px-2 py-3 text-center text-sm font-semibold text-white font-['Exo'] whitespace-nowrap bg-[#3a3d42]">
                              {TEXT.removeColumn}
                            </th>
                          </tr>

                        </thead>
                        <tbody className="divide-y divide-metal">
                          {selectedFilaments.map((filament) => (
                            <tr key={filament.slug} className="hover:bg-metal/30 transition-colors relative border-l border-l-white/20">
                              {/* 左端のアクセントバー（疑似要素で実装） */}
                              <td className="px-3 py-3 whitespace-nowrap relative before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-accent/60">
                                <Link 
                                  to={isEnglish ? `/en/filament/${filament.slug}` : `/filament/${filament.slug}`}
                                  className="text-accent hover:text-accent-orange font-semibold font-['Exo'] transition-colors"
                                >
                                  {filament.name}
                                </Link>
                              </td>

                              {/* 印刷温度 */}
                              <td className="px-3 py-3">
                                <div className="flex flex-col gap-1">
                                  <RatingStars rating={filament.printTempRating} />
                                  {filament.printTempRating > 0 ? (
                                    <div className="text-xs text-metal-light">
                                      <div>{TEXT.nozzle}: {filament.nozzleTemp}</div>
                                      <div>{TEXT.bed}: {filament.bedTemp}</div>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-metal-light">{TEXT.notEntered}</span>
                                  )}
                                </div>
                              </td>

                              {/* 反り */}
                              <td className="px-3 py-3">
                                <div className="flex flex-col gap-1">
                                  <RatingStars rating={filament.warpingRating} />
                                  {filament.warpingRating > 0 ? (
                                    (isEnglish && filament.warpingNoteEn) || filament.warpingNote ? (
                                      <span className="text-xs text-metal-light">{isEnglish && filament.warpingNoteEn ? filament.warpingNoteEn : filament.warpingNote}</span>
                                    ) : null
                                  ) : (
                                    <span className="text-xs text-metal-light">{TEXT.notEntered}</span>
                                  )}
                                </div>
                              </td>

                              {/* 強度 */}
                              <td className="px-3 py-3">
                                <div className="flex flex-col gap-1">
                                  <RatingStars rating={filament.strengthRating} />
                                  {filament.strengthRating > 0 ? (
                                    filament.bendStrengthMPa ? (
                                      <span className="text-xs text-metal-light">
                                        {formatBendStrength(filament.slug, filament.bendStrengthMPa)}
                                      </span>
                                    ) : (
                                      <span className="text-xs text-metal-light">{TEXT.notMeasured}</span>
                                    )
                                  ) : (
                                    <span className="text-xs text-metal-light">
                                      {isEnglish && filament.strengthNoteEn ? filament.strengthNoteEn : filament.strengthNote || TEXT.notEntered}
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* 弾性率 */}
                              <td className="px-3 py-3">
                                <div className="flex flex-col gap-1">
                                  <RatingStars rating={filament.modulusRating} />
                                  {filament.flexuralModulusMPa ? (
                                    <span className="text-xs text-metal-light">
                                      {formatFlexuralModulus(filament.slug, filament.flexuralModulusMPa)}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-metal-light">{TEXT.notMeasured}</span>
                                  )}
                                </div>
                              </td>

                              {/* クリープ耐性 */}
                              <td className="px-3 py-3">
                                <div className="flex flex-col gap-1">
                                  <RatingStars rating={filament.creepResistanceRating} />
                                  {filament.creepResistanceRating > 0 ? (
                                    (isEnglish && filament.creepNoteEn) || filament.creepNote ? (
                                      <span className="text-xs text-metal-light">{isEnglish && filament.creepNoteEn ? filament.creepNoteEn : filament.creepNote}</span>
                                    ) : null
                                  ) : (
                                    <span className="text-xs text-metal-light">{TEXT.notEntered}</span>
                                  )}
                                </div>
                              </td>

                              {/* 耐熱 */}
                              <td className="px-3 py-3">
                                <div className="flex flex-col gap-1">
                                  <RatingStars rating={filament.heatResistanceRating} />
                                  {filament.heatResistanceRating > 0 ? (
                                    filament.heatResistanceTemp ? (
                                      <span className="text-xs text-metal-light">
                                        {TEXT.heatResistanceTemp}: {filament.heatResistanceTemp}°C
                                      </span>
                                    ) : (
                                      <span className="text-xs text-metal-light">{TEXT.notMeasured}</span>
                                    )
                                  ) : (
                                    <span className="text-xs text-metal-light">{TEXT.notEntered}</span>
                                  )}
                                </div>
                              </td>

                              {/* 右端の解除ボタン（行内に配置） */}
                              <td className="px-2 py-3 bg-[#3a3d42]">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeFilament(filament.slug);
                                  }}
                                  className="w-7 h-7 rounded-full bg-transparent border border-white/30 hover:border-accent-orange hover:bg-accent-orange/10 text-white/70 hover:text-accent-orange transition-all cursor-pointer flex items-center justify-center flex-shrink-0"
                                  aria-label={`${filament.name}を比較から削除`}
                                  title={TEXT.remove}
                                >
                                  <i className="ri-close-line text-base"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="px-6 py-3 bg-[#3a3d42] border-t border-metal">
                    <p className="text-metal-light text-xs flex items-center gap-2">
                      <i className="ri-information-line text-accent"></i>
                      {TEXT.sortInfo}
                    </p>
                  </div>
                </div>

                {/* モバイル表示 - 表形式を維持 */}
                <div className="sm:hidden tech-card overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-[#3a3d42]">
                          <th 
                            onClick={() => handleHeaderClick('none')}
                            className="px-2 py-2 text-left text-[10px] font-semibold text-white font-['Exo'] whitespace-nowrap cursor-pointer active:bg-metal/50 transition-colors min-w-[80px]"
                          >
                            <div className="flex items-center gap-1">
                              <span>{TEXT.materialColumn}</span>
                              {sortKey === 'none' && (
                                <i className="ri-refresh-line text-white text-[10px] bg-accent/20 rounded-full w-3.5 h-3.5 flex items-center justify-center flex-shrink-0"></i>
                              )}
                            </div>
                          </th>
                          <th 
                            onClick={() => handleHeaderClick('printTemp')}
                            className="px-2 py-2 text-left text-[10px] font-semibold text-white font-['Exo'] whitespace-nowrap cursor-pointer active:bg-metal/50 transition-colors min-w-[70px]"
                          >
                            <div className="flex items-center gap-1">
                              <span>{TEXT.tempColumn}</span>
                              {sortKey === 'printTemp' && (
                                <i className={`text-white text-[10px] bg-accent/20 rounded-full w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 ${sortOrder === 'desc' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'}`}></i>
                              )}
                            </div>
                          </th>
                          <th 
                            onClick={() => handleHeaderClick('warping')}
                            className="px-2 py-2 text-left text-[10px] font-semibold text-white font-['Exo'] whitespace-nowrap cursor-pointer active:bg-metal/50 transition-colors min-w-[60px]"
                          >
                            <div className="flex items-center gap-1">
                              <span>{TEXT.warpingColumn}</span>
                              {sortKey === 'warping' && (
                                <i className={`text-white text-[10px] bg-accent/20 rounded-full w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 ${sortOrder === 'desc' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'}`}></i>
                              )}
                            </div>
                          </th>
                          <th 
                            onClick={() => handleHeaderClick('strength')}
                            className="px-1 py-2 text-left text-[10px] font-semibold text-white font-['Exo'] whitespace-nowrap cursor-pointer active:bg-metal/50 transition-colors min-w-[55px]"
                          >
                            <div className="flex items-center gap-1">
                              <span>{TEXT.strengthColumn}</span>
                              {sortKey === 'strength' && (
                                <i className={`text-white text-[10px] bg-accent/20 rounded-full w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 ${sortOrder === 'desc' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'}`}></i>
                              )}
                            </div>
                          </th>
                          <th 
                            onClick={() => handleHeaderClick('modulus')}
                            className="px-1 py-2 text-left text-[10px] font-semibold text-white font-['Exo'] whitespace-nowrap cursor-pointer active:bg-metal/50 transition-colors min-w-[50px]"
                          >
                            <div className="flex items-center gap-1">
                              <span>{TEXT.modulusColumn}</span>
                              {sortKey === 'modulus' && (
                                <i className={`text-white text-[10px] bg-accent/20 rounded-full w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 ${sortOrder === 'desc' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'}`}></i>
                              )}
                            </div>
                          </th>
                          <th 
                            onClick={() => handleHeaderClick('creep')}
                            className="px-2 py-2 text-left text-[10px] font-semibold text-white font-['Exo'] whitespace-nowrap cursor-pointer active:bg-metal/50 transition-colors min-w-[70px]"
                          >
                            <div className="flex items-center gap-1">
                              <span>{TEXT.creepColumn}</span>
                              {sortKey === 'creep' && (
                                <i className={`text-white text-[10px] bg-accent/20 rounded-full w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 ${sortOrder === 'desc' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'}`}></i>
                              )}
                            </div>
                          </th>
                          <th 
                            onClick={() => handleHeaderClick('heat')}
                            className="px-2 py-2 text-left text-[10px] font-semibold text-white font-['Exo'] whitespace-nowrap cursor-pointer active:bg-metal/50 transition-colors min-w-[60px]"
                          >
                            <div className="flex items-center gap-1">
                              <span>{TEXT.heatColumn}</span>
                              {sortKey === 'heat' && (
                                <i className={`text-white text-[10px] bg-accent/20 rounded-full w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 ${sortOrder === 'desc' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'}`}></i>
                              )}
                            </div>
                          </th>
                          <th className="px-2 py-2 text-center text-[10px] font-semibold text-white font-['Exo'] whitespace-nowrap bg-[#3a3d42]">
                            {TEXT.removeColumn}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-metal">
                        {selectedFilaments.map((filament) => (
                          <tr key={filament.slug} className="active:bg-metal/30 transition-colors relative border-l border-l-white/20">
                            {/* 左端のアクセントバー（疑似要素で実装） */}
                            <td className="px-2 py-2 whitespace-nowrap relative before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-0.5 before:bg-accent/60">
                              <Link 
                                to={isEnglish ? `/en/filament/${filament.slug}` : `/filament/${filament.slug}`}
                                className="text-accent hover:text-accent-orange font-semibold font-['Exo'] text-[11px] transition-colors"
                              >
                                {filament.name}
                              </Link>
                            </td>

                            {/* 印刷温度 */}
                            <td className="px-2 py-2">
                              <div className="flex flex-col gap-0.5">
                                <RatingStars rating={filament.printTempRating} size="small" />
                                {filament.printTempRating > 0 ? (
                                  <div className="text-[9px] text-metal-light whitespace-nowrap">
                                    <div>N:{filament.nozzleTemp}</div>
                                    <div>B:{filament.bedTemp}</div>
                                  </div>
                                ) : (
                                  <span className="text-[9px] text-metal-light whitespace-nowrap">{TEXT.notEntered}</span>
                                )}
                              </div>
                            </td>

                            {/* 反り */}
                            <td className="px-2 py-2">
                              <div className="flex flex-col gap-0.5">
                                <RatingStars rating={filament.warpingRating} size="small" />
                                {filament.warpingRating > 0 ? (
                                  (isEnglish && filament.warpingNoteEn) || filament.warpingNote ? (
                                    <span className="text-[9px] text-metal-light line-clamp-1">{isEnglish && filament.warpingNoteEn ? filament.warpingNoteEn : filament.warpingNote}</span>
                                  ) : null
                                ) : (
                                  <span className="text-[9px] text-metal-light whitespace-nowrap">{TEXT.notEntered}</span>
                                )}
                              </div>
                            </td>

                            {/* 強度 */}
                            <td className="px-1 py-2">
                              <div className="flex flex-col gap-0.5">
                                <RatingStars rating={filament.strengthRating} size="small" />
                                {filament.strengthRating > 0 ? (
                                  filament.bendStrengthMPa ? (
                                    <span className="text-[9px] text-metal-light whitespace-nowrap">
                                      {formatBendStrength(filament.slug, filament.bendStrengthMPa)}
                                    </span>
                                  ) : (
                                    <span className="text-[9px] text-metal-light whitespace-nowrap">{TEXT.notMeasured}</span>
                                  )
                                ) : (
                                  <span className="text-[9px] text-metal-light whitespace-nowrap">
                                    {isEnglish && filament.strengthNoteEn ? filament.strengthNoteEn : filament.strengthNote || TEXT.notEntered}
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* 弾性率 */}
                            <td className="px-1 py-2">
                              <div className="flex flex-col gap-0.5">
                                <RatingStars rating={filament.modulusRating} size="small" />
                                {filament.flexuralModulusMPa ? (
                                  <span className="text-[9px] text-metal-light whitespace-nowrap">
                                    {formatFlexuralModulus(filament.slug, filament.flexuralModulusMPa)}
                                  </span>
                                ) : (
                                  <span className="text-[9px] text-metal-light whitespace-nowrap">{TEXT.notMeasured}</span>
                                )}
                              </div>
                            </td>

                            {/* クリープ耐性 */}
                            <td className="px-2 py-2">
                              <div className="flex flex-col gap-0.5">
                                <RatingStars rating={filament.creepResistanceRating} size="small" />
                                {filament.creepResistanceRating > 0 ? (
                                  (isEnglish && filament.creepNoteEn) || filament.creepNote ? (
                                    <span className="text-[9px] text-metal-light line-clamp-1">{isEnglish && filament.creepNoteEn ? filament.creepNoteEn : filament.creepNote}</span>
                                  ) : null
                                ) : (
                                  <span className="text-[9px] text-metal-light whitespace-nowrap">{TEXT.notEntered}</span>
                                )}
                              </div>
                            </td>

                            {/* 耐熱 */}
                            <td className="px-2 py-2">
                              <div className="flex flex-col gap-0.5">
                                <RatingStars rating={filament.heatResistanceRating} size="small" />
                                {filament.heatResistanceRating > 0 ? (
                                  filament.heatResistanceTemp ? (
                                    <span className="text-[9px] text-metal-light whitespace-nowrap">
                                      {TEXT.heatResistanceTemp}: {filament.heatResistanceTemp}°C
                                    </span>
                                  ) : (
                                    <span className="text-[9px] text-metal-light whitespace-nowrap">{TEXT.notMeasured}</span>
                                  )
                                ) : (
                                  <span className="text-[9px] text-metal-light whitespace-nowrap">{TEXT.notEntered}</span>
                                )}
                              </div>
                            </td>

                            {/* 右端の解除ボタン（行内に配置） */}
                            <td className="px-2 py-2 bg-[#3a3d42]">
                              <div className="flex items-center justify-center">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeFilament(filament.slug);
                                  }}
                                  className="w-6 h-6 rounded-full bg-transparent border border-white/30 hover:border-accent-orange hover:bg-accent-orange/10 text-white/70 hover:text-accent-orange transition-all cursor-pointer flex items-center justify-center flex-shrink-0"
                                  aria-label={`${filament.name}を比較から削除`}
                                  title={TEXT.remove}
                                >
                                  <i className="ri-close-line text-[12px]"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-2 py-2 bg-[#3a3d42] border-t border-metal">
                    <p className="text-metal-light text-[9px] flex items-center gap-1">
                      <i className="ri-information-line text-accent text-[10px]"></i>
                      {TEXT.sortInfoMobile}
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div className="metal-panel p-8 text-center">
                <i className="ri-information-line text-accent text-4xl mb-4"></i>
                <p className="text-metal-light text-sm sm:text-base">{TEXT.noFilamentsSelected}</p>
              </div>
            )}

            {/* 3) 評価基準 */}
            <div className="metal-panel p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2 flex items-center gap-2">
                <i className="ri-information-line text-accent"></i>
                {TEXT.ratingCriteria}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 text-xs sm:text-sm">
                <div>
                  <span className="text-metal-light">{TEXT.printTempCriteria}</span>
                </div>
                <div>
                  <span className="text-metal-light">{TEXT.warpingCriteria}</span>
                </div>
                <div>
                  <span className="text-metal-light">{TEXT.strengthCriteria}</span>
                </div>
                <div>
                  <span className="text-metal-light">{TEXT.creepCriteria}</span>
                </div>
                <div>
                  <span className="text-metal-light">{TEXT.heatCriteria}</span>
                </div>
                <div>
                  <span className="text-metal-light">{TEXT.notEnteredCriteria}</span>
                </div>
              </div>
            </div>

            {/* 4) Stress-Strain グラフ */}
            {selectedSlugs.length > 0 && (
              <div className="metal-panel p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2 flex items-center gap-2">
                  <i className="ri-line-chart-line text-accent"></i>
                  {TEXT.stressStrainTitle}
                </h2>
                <p className="text-metal-light text-xs sm:text-sm mb-4">
                  {TEXT.stressStrainDescription}
                </p>

                {/* パターン切替ボタン */}
                <div className="flex items-center gap-2 mb-6">
                  <span className="text-metal-light text-sm">{TEXT.measurementDirection}</span>
                  <div className="flex gap-2">
                    {(['pattern1', 'pattern2', 'pattern3'] as Pattern[]).map(pattern => {
                      // このパターンでデータがあるフィラメント数をカウント
                      const dataCount = selectedSlugs.filter(slug => {
                        const patternMap = stressStrainCache.get(slug);
                        return patternMap && patternMap.get(pattern) !== null;
                      }).length;
                      
                      const hasData = dataCount > 0;
                      const isSelected = selectedPattern === pattern;
                      
                      return (
                        <button
                          key={pattern}
                          onClick={() => setSelectedPattern(pattern)}
                          disabled={!hasData}
                          className={`px-3 py-1.5 text-sm font-medium rounded transition-colors cursor-pointer whitespace-nowrap ${
                            isSelected
                              ? 'bg-accent text-white'
                              : hasData
                              ? 'bg-transparent border border-metal-light/40 text-metal-light hover:border-metal-light hover:bg-metal-light/10 hover:text-white'
                              : 'bg-transparent border border-metal-light/20 text-metal-light/40 cursor-not-allowed'
                          }`}
                        >
                          {patternLabels[pattern]}
                          {!hasData && ` ${TEXT.noData}`}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {loadingData.size > 0 && (
                  <div className="flex items-center justify-center py-8">
                    <div className="flex items-center gap-3">
                      <i className="ri-loader-4-line text-accent text-2xl animate-spin"></i>
                      <span className="text-metal-light text-sm">{TEXT.loadingData}</span>
                    </div>
                  </div>
                )}

                {loadingData.size === 0 && stressStrainDatasets.length > 0 && (
                  <StressStrainChart datasets={stressStrainDatasets} />
                )}

                {loadingData.size === 0 && stressStrainDatasets.length === 0 && (
                  <div className="flex items-start space-x-3 bg-metal p-4 sm:p-6 rounded">
                    <i className="ri-information-line text-metal-light text-lg mt-0.5"></i>
                    <p className="text-metal-light text-xs sm:text-sm">
                      {TEXT.noStressStrainData.replace('{pattern}', patternLabels[selectedPattern])}
                    </p>
                  </div>
                )}

                {loadingData.size === 0 && stressStrainDatasets.length > 0 && selectedSlugs.length > stressStrainDatasets.length && (
                  <div className="mt-6 flex items-start space-x-3 bg-metal p-3 sm:p-4 rounded">
                    <i className="ri-information-line text-metal-light text-lg mt-0.5"></i>
                    <p className="text-metal-light text-xs sm:text-sm">
                      {TEXT.partialDataInfo.replace('{count}', String(selectedSlugs.length - stressStrainDatasets.length)).replace('{pattern}', patternLabels[selectedPattern])}
                    </p>
                  </div>
                )}

                {/* ダウンロードページへのリンク */}
                <div className="mt-6 flex items-center justify-between flex-wrap gap-4 bg-metal/40 border border-accent/20 p-3 sm:p-4 rounded">
                  <div className="flex items-center gap-3">
                    <i className="ri-download-2-line text-accent text-xl flex-shrink-0"></i>
                    <div>
                      <p className="text-white font-semibold text-xs sm:text-sm">{TEXT.downloadPageTitle}</p>
                      <p className="text-metal-light text-xs">{TEXT.downloadPageDesc}</p>
                    </div>
                  </div>
                  <Link
                    to={isEnglish ? '/en/filament/data-download' : '/filament/data-download'}
                    className="flex items-center gap-2 px-3 py-1.5 bg-accent/20 border border-accent/60 hover:bg-accent/30 hover:border-accent text-accent text-xs sm:text-sm font-medium rounded transition-colors whitespace-nowrap"
                  >
                    <i className="ri-file-download-line"></i>
                    {TEXT.downloadPageTitle}
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>

                {/* データ利用に関する注意書き */}
                <div className="mt-6 flex items-start space-x-3 bg-accent/10 border border-accent/30 p-3 sm:p-4 rounded">
                  <i className="ri-alert-line text-accent text-lg mt-0.5 flex-shrink-0"></i>
                  <div className="text-xs sm:text-sm">
                    <p className="text-white font-semibold mb-1">{TEXT.dataUsageTitle}</p>
                    <p className="text-metal-light">
                      {TEXT.dataUsageText.split('{link}')[0]}
                      <Link to="/data-policy" className="text-accent hover:text-accent-orange underline transition-colors">{TEXT.dataUsageLink}</Link>
                      {TEXT.dataUsageText.split('{link}')[1]}
                    </p>
                  </div>
                </div>
              </div>
            )}


            {/* 注意事項 */}
            <div className="metal-panel p-4 sm:p-6">
              <div className="flex items-start space-x-3">
                <i className="ri-information-line text-accent text-xl mt-1"></i>
                <div className="space-y-2">
                  <h4 className="text-white font-semibold font-['Exo'] text-sm sm:text-base">
                    {TEXT.notesTitle}
                  </h4>
                  <ul className="text-metal-light text-xs sm:text-sm space-y-1 list-disc list-inside">
                    <li>{TEXT.note1}</li>
                    <li>{TEXT.note2}</li>
                    <li>{TEXT.note3}</li>
                    <li>{TEXT.note4}</li>
                    <li>{TEXT.note5}</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 戻るリンク */}
            <div className="text-center">
              <Link 
                to={isEnglish ? "/en/filament" : "/filament"}
                className="inline-flex items-center gap-2 text-accent hover:text-accent-orange font-medium transition-colors text-sm sm:text-base"
              >
                <i className="ri-arrow-left-line"></i>
                {TEXT.backToList}
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default FilamentComparePage;
